'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';

import { RatingsSummary } from '@ui/components/features/review/ratingsSummary';
import { ReviewCard } from '@ui/components/features/review/reviewCard';
import { ReviewDetailDialog } from '@ui/components/features/review/reviewDetailDialog';
import { useCurrentUser } from '@website/hooks/useCurrentUser';

import { useReviews } from '../../../../../hooks/useReviews';
import type { ActivityRatingData } from '../../../../../services/activities.service';
import type { ReviewItem } from '../../../../../services/reviews.service';

interface RatingsAndReviewsSectionProps {
  activityId: string;
  activityTitle?: string;
  rating?: ActivityRatingData;
  professionalId?: string;
}

function formatReviewDate(timestamp: number, locale: string): string {
  return new Date(timestamp).toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

export default function RatingsAndReviewsSection({
  activityId,
  activityTitle,
  rating,
  professionalId
}: RatingsAndReviewsSectionProps) {
  const t = useTranslations('ActivityDetail.ratings');
  const locale = useLocale();
  const [selectedReview, setSelectedReview] = useState<ReviewItem | null>(null);
  const { data: user } = useCurrentUser();

  const isOwner = !!user && user.isProfessional && !!professionalId && user.id === professionalId;

  const { data: reviewsData } = useReviews(activityId, { limit: 3, status: 'approved' });

  const reviews = reviewsData?.reviews ?? [];
  const totalReviews = reviewsData?.total ?? rating?.ratingTotal ?? 0;

  return (
    <section className="w-full space-y-6 md:space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="text-base md:text-lg font-semibold text-neutral-800">{t('ratingsAndReviews')}</h3>
        {isOwner && (
          <a
            href={`/${locale}/activity/${activityId}/reviews`}
            className="text-sm font-medium text-primary hover:underline"
          >
            {t('editReviews')}
          </a>
        )}
      </div>

      {rating && rating.ratingTotal > 0 && (
        <RatingsSummary
          average={rating.ratingAverage}
          total={rating.ratingTotal}
          distribution={rating.ratingDist}
          hideTitle
        />
      )}

      {reviews.length > 0 && (
        <div className="flex flex-col gap-4">
          <h4 className="text-sm md:text-base font-semibold text-neutral-800">{t('reviews')}</h4>
          <div className="flex flex-col divide-y divide-neutral-200">
            {reviews.map((review) => (
              <ReviewCard
                key={review.id}
                userName={review.userName}
                userAvatarUrl={review.userAvatarUrl}
                date={formatReviewDate(review.createdAt, locale)}
                rating={review.rating}
                comment={review.comment}
                tags={review.tags}
                onClick={() => setSelectedReview(review)}
                onSeeMore={() => setSelectedReview(review)}
                seeMoreLabel={t('seeMore')}
                seeLessLabel={t('seeLess')}
                userLikedLabel={t('userLiked')}
                className="py-4 first:pt-0"
              />
            ))}
          </div>

          {totalReviews > 0 && (
            <a
              href={`/${locale}/activity/${activityId}/reviews`}
              className="cursor-pointer text-base font-bold text-primary hover:underline"
            >
              {t('allReviews', { count: totalReviews })}
            </a>
          )}
        </div>
      )}

      {selectedReview && (
        <ReviewDetailDialog
          isOpen={!!selectedReview}
          onClose={() => setSelectedReview(null)}
          userName={selectedReview.userName}
          activityTitle={activityTitle}
          date={formatReviewDate(selectedReview.createdAt, locale)}
          rating={selectedReview.rating}
          comment={selectedReview.comment}
          tags={selectedReview.tags}
          title={t('customerExperience')}
          userLikedLabel={t('userLiked')}
        />
      )}
    </section>
  );
}
